import React, { useEffect, useMemo, useState } from 'react'
import FormCompanyLogo from './FormCompanyLogo'
import { toast } from 'react-toastify'
import { qhseLibraryApi, LibraryTemplateFilters } from '../../../QHSE/core/libraryApi'
import type { Fields, Reports } from '../../../QHSE/core/libraryModels'
import {
  createQhseFormSubmission,
  QHSEFormSubmissionPayload,
  QHSEFormTemplate,
} from '../../../QHSE/core/forms_requests'
import SearchableSelect from '../../../../shared/components/SearchableSelect'
import PortSelect from '../../../../shared/components/PortSelect'
import { isPortField } from '../../../../shared/utils/portFieldUtils'
import { useGlobalFilter } from '../../../../shared/context/GlobalFilterContext'

interface Props {
  isOpen: boolean
  onClose: () => void
  companyGroupAdminId?: number
  companyAdminId?: number
  vesselId?: number
  crewId?: number
  createdByUserId?: number
  onSubmitted?: () => void
}

type FieldValue = string | number | boolean | string[]

type FieldErrorMap = Record<number, string>

const isEmptyValue = (value: FieldValue | undefined) => {
  if (value === undefined || value === null) return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'boolean') return value === false
  return String(value).trim() === ''
}

const coerceValueText = (value: FieldValue | undefined) => {
  if (value === undefined || value === null) return ''
  if (Array.isArray(value)) return JSON.stringify(value)
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

const sortByOrder = <T,>(items: T[]) =>
  [...items].sort((a, b) => {
    const orderA = typeof (a as { order?: number }).order === 'number' ? (a as { order?: number }).order! : 0
    const orderB = typeof (b as { order?: number }).order === 'number' ? (b as { order?: number }).order! : 0
    return orderA - orderB
  })

const normalizeFieldType = (field: Fields) => String(field.fieldType || '').toLowerCase()

const renderFieldInput = (
  field: Fields,
  value: FieldValue | undefined,
  onChange: (next: FieldValue) => void
) => {
  const fieldType = normalizeFieldType(field)
  const isReadOnly = field.readOnly
  const options = (field.optionsJson || []).filter(Boolean)

  if (fieldType === 'textarea') {
    return (
      <textarea
        className='form-control'
        rows={3}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isReadOnly}
      />
    )
  }

  if (fieldType === 'select') {
    return (
      <SearchableSelect
        className='form-select'
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isReadOnly}
      >
        <option value=''>Select</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </SearchableSelect>
    )
  }

  if (fieldType === 'radio') {
    return (
      <div className='d-flex flex-wrap gap-3'>
        {options.map((opt) => (
          <label key={opt} className='form-check form-check-inline m-0'>
            <input
              className='form-check-input'
              type='radio'
              name={`field-${field.id}`}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
              disabled={isReadOnly}
            />
            <span className='form-check-label'>{opt}</span>
          </label>
        ))}
      </div>
    )
  }

  if (fieldType === 'checkbox') {
    if (options.length > 0) {
      const selected = Array.isArray(value) ? value : []
      return (
        <div className='d-flex flex-wrap gap-3'>
          {options.map((opt) => {
            const checked = selected.includes(opt)
            return (
              <label key={opt} className='form-check form-check-inline m-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={checked}
                  onChange={() => {
                    const next = checked ? selected.filter((item) => item !== opt) : [...selected, opt]
                    onChange(next)
                  }}
                  disabled={isReadOnly}
                />
                <span className='form-check-label'>{opt}</span>
              </label>
            )
          })}
        </div>
      )
    }

    return (
      <div className='form-check'>
        <input
          className='form-check-input'
          type='checkbox'
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
          disabled={isReadOnly}
        />
        <label className='form-check-label'>Yes</label>
      </div>
    )
  }

  if (fieldType === 'number') {
    return (
      <div className='input-group'>
        <input
          type='number'
          className='form-control'
          value={typeof value === 'number' || typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          disabled={isReadOnly}
        />
        {field.unit ? <span className='input-group-text'>{field.unit}</span> : null}
      </div>
    )
  }

  if (fieldType === 'date') {
    return (
      <input
        type='date'
        className='form-control'
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isReadOnly}
      />
    )
  }

  if (fieldType === 'time') {
    return (
      <input
        type='time'
        className='form-control'
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isReadOnly}
      />
    )
  }

  if (fieldType === 'datetime') {
    return (
      <input
        type='datetime-local'
        className='form-control'
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={isReadOnly}
      />
    )
  }

  return (
    <input
      type='text'
      className='form-control'
      value={typeof value === 'string' || typeof value === 'number' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={isReadOnly}
    />
  )
}

const LibraryFormSubmissionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  companyGroupAdminId: propCompanyGroupAdminId,
  companyAdminId: propCompanyAdminId,
  vesselId: propVesselId,
  crewId,
  createdByUserId,
  onSubmitted,
}) => {
  const { state: globalState } = useGlobalFilter()

  const companyGroupAdminId = useMemo(() => {
    if (propCompanyGroupAdminId) return propCompanyGroupAdminId
    if (globalState.company?.type === 'company_group') return Number(globalState.company.id)
    if ((globalState.vessel as any)?.companyGroupAdminId) return Number((globalState.vessel as any).companyGroupAdminId)
    if ((globalState.vessel as any)?.companyGroupId) return Number((globalState.vessel as any).companyGroupId)
    if ((globalState.vessel as any)?.companyGroupAdmin?.id) return Number((globalState.vessel as any).companyGroupAdmin.id)
    if (globalState.company?.id) return Number(globalState.company.id)
    return undefined
  }, [propCompanyGroupAdminId, globalState.company, globalState.vessel])

  const companyAdminId = useMemo(() => {
    if (propCompanyAdminId) return propCompanyAdminId
    if (globalState.company?.type === 'company_admin') return Number(globalState.company.id)
    if ((globalState.vessel as any)?.companyAdminId) return Number((globalState.vessel as any).companyAdminId)
    if ((globalState.vessel as any)?.companyAdmin?.id) return Number((globalState.vessel as any).companyAdmin.id)
    return undefined
  }, [propCompanyAdminId, globalState.company, globalState.vessel])

  const vesselId = useMemo(() => {
    if (propVesselId) return propVesselId
    if (globalState.vessel?.id) return Number(globalState.vessel.id)
    return undefined
  }, [propVesselId, globalState.vessel])

  const [templates, setTemplates] = useState<QHSEFormTemplate[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [menus, setMenus] = useState<Reports[]>([])
  const [loadingMenus, setLoadingMenus] = useState(false)
  const [remarks, setRemarks] = useState('')
  const [values, setValues] = useState<Record<number, FieldValue>>({})
  const [errors, setErrors] = useState<FieldErrorMap>({})
  const [submitting, setSubmitting] = useState(false)

  const visibleTemplates = useMemo(
    () => templates.filter((tpl) => tpl.isActive !== false),
    [templates]
  )

  const sections = useMemo(() => {
    const activeMenus = menus.filter((menu) => menu.isActive)
    return sortByOrder(activeMenus).map((menu) => {
      const submenus = sortByOrder(menu.submenus ?? []).filter((sub) => sub.isActive)
      return {
        id: menu.id,
        title: menu.name,
        submenus,
      }
    })
  }, [menus])

  const allFields = useMemo(() => {
    const fields: Fields[] = []
    sections.forEach((section) => {
      section.submenus.forEach((submenu) => {
        sortByOrder(submenu.fields ?? [])
          .filter((field) => field.isActive)
          .forEach((field) => fields.push(field))
      })
    })
    return fields
  }, [sections])

  useEffect(() => {
    if (!isOpen) return
    if (!companyGroupAdminId) {
      setTemplates([])
      return
    }

    let active = true
    const load = async () => {
      setLoadingTemplates(true)
      try {
        const filters: LibraryTemplateFilters = {
          companyGroupAdminId,
          companyAdminId,
        }
        const list = await qhseLibraryApi.getTemplateList(filters)
        if (!active) return
        setTemplates(list as QHSEFormTemplate[])
        if (list.length) {
          setSelectedTemplateId((prev) => (prev == null ? list[0].id : prev))
        }
      } catch (err: any) {
        if (!active) return
        toast.error('Unable to load library templates')
      } finally {
        if (active) setLoadingTemplates(false)
      }
    }

    load()
    return () => {
      active = false
    }
  }, [companyAdminId, companyGroupAdminId, isOpen])

  useEffect(() => {
    if (selectedTemplateId == null) {
      setMenus([])
      return
    }

    let active = true
    const loadMenus = async () => {
      setLoadingMenus(true)
      try {
        const list = await qhseLibraryApi.getReportsForTemplate(Number(selectedTemplateId))
        if (!active) return
        setMenus(list)
        setValues({})
        setErrors({})
        setRemarks('')
      } catch (err: any) {
        if (!active) return
        toast.error('Unable to load template fields')
      } finally {
        if (active) setLoadingMenus(false)
      }
    }

    loadMenus()
    return () => {
      active = false
    }
  }, [selectedTemplateId])

  const handleSubmit = async () => {
    if (selectedTemplateId == null) {
      toast.error('Please select a library template')
      return
    }

    const nextErrors: FieldErrorMap = {}
    allFields.forEach((field) => {
      if (field.required && isEmptyValue(values[field.id])) {
        nextErrors[field.id] = 'Required'
      }
    })

    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) {
      toast.error('Please complete required fields')
      return
    }

    const payload: QHSEFormSubmissionPayload = {
      templateId: Number(selectedTemplateId),
      status: 'SUBMITTED',
      createdByUserId,
      companyGroupAdminId,
      companyAdminId,
      vesselId,
      crewId,
      remarks: remarks.trim() || undefined,
      values: allFields.map((field) => ({
        fieldId: field.id,
        valueText: coerceValueText(values[field.id]),
      })),
    }

    try {
      setSubmitting(true)
      await createQhseFormSubmission(payload)
      toast.success('Library form submitted')
      onSubmitted?.()
      onClose()
    } catch (err: any) {
      toast.error('Unable to submit library form')
    } finally {
      setSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className='lf-overlay'
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className='lf-panel' role='dialog' aria-modal='true' onMouseDown={(e) => e.stopPropagation()}>
        <div className='lf-header'>
          <div>
            <div className='lf-title'>Library Form Submission</div>
            <div className='lf-subtitle'>Fill and submit dynamic library forms.</div>
          </div>
          <button className='lf-close' type='button' onClick={onClose} aria-label='Close'>
            x
          </button>
        </div>

        <div className='lf-body'>
          {!companyGroupAdminId ? (
            <div className='alert alert-warning mb-0'>Please select a company group from the top filter to load library templates.</div>
          ) : loadingTemplates ? (
            <div className='d-flex align-items-center gap-2 text-muted'>
              <span className='spinner-border spinner-border-sm' />
              Loading templates...
            </div>
          ) : visibleTemplates.length === 0 ? (
            <div className='lf-empty'>
              <div className='lf-empty-title'>No forms found</div>
              <div className='lf-empty-subtitle'>
                Your company has not created any library forms yet.
              </div>
            </div>
          ) : (
            <FormCompanyLogo vesselId={vesselId}>
              <div className='row g-3 mb-4'>
                <div className='col-12 col-lg-6'>
                  <label className='form-label fw-semibold'>Template</label>
                  <SearchableSelect
                    className='form-select'
                    value={selectedTemplateId ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value
                      setSelectedTemplateId(raw ? Number(raw) : null)
                    }}
                  >
                    {visibleTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                        {template.formType ? ` (${template.formType})` : ''}
                      </option>
                    ))}
                  </SearchableSelect>
                </div>
              </div>

              {loadingMenus ? (
                <div className='d-flex align-items-center gap-2 text-muted'>
                  <span className='spinner-border spinner-border-sm' />
                  Loading fields...
                </div>
              ) : sections.length === 0 ? (
                <div className='alert alert-light mb-0'>No fields are configured for this template.</div>
              ) : (
                <div className='d-flex flex-column gap-4'>
                  {sections.map((section) => (
                    <div key={section.id} className='border rounded p-3'>
                      <div className='fw-semibold mb-2'>{section.title}</div>
                      <div className='d-flex flex-column gap-3'>
                        {section.submenus.map((submenu) => (
                          <div key={submenu.id}>
                            <div className='text-muted fw-semibold mb-2'>{submenu.name}</div>
                            <div className='row g-3'>
                              {sortByOrder(submenu.fields ?? [])
                                .filter((field) => field.isActive)
                                .map((field) => (
                                  <div key={field.id} className='col-12 col-md-6'>
                                    <label className='form-label fw-semibold'>
                                      {field.label}
                                      {field.required ? <span className='text-danger ms-1'>*</span> : null}
                                    </label>
                                    {renderFieldInput(field, values[field.id], (next) => {
                                      setValues((prev) => ({ ...prev, [field.id]: next }))
                                      if (errors[field.id]) {
                                        setErrors((prev) => {
                                          const updated = { ...prev }
                                          delete updated[field.id]
                                          return updated
                                        })
                                      }
                                    })}
                                    {errors[field.id] ? (
                                      <div className='text-danger small mt-1'>{errors[field.id]}</div>
                                    ) : null}
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className='mt-4'>
                <label className='form-label fw-semibold'>Remarks (optional)</label>
                <input
                  className='form-control'
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder='Add remarks'
                />
              </div>
            </FormCompanyLogo>
          )}
        </div>

        <div className='lf-footer'>
          <button className='btn btn-light' type='button' onClick={onClose}>
            Cancel
          </button>
          <button
            className='btn btn_primary'
            type='button'
            onClick={handleSubmit}
            disabled={submitting || loadingTemplates || loadingMenus || !visibleTemplates.length}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>

        <style>{`
        .lf-overlay {
          position: fixed;
          inset: 0;
          z-index: 1050;
          background: rgba(0,0,0,0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }

        .lf-panel {
          width: min(1200px, calc(100vw - 36px));
          height: min(88vh, 900px);
          background: #fff;
          border-radius: 14px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }

        .lf-header {
          padding: 18px 20px 10px 20px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          border-bottom: 1px solid #eef0f5;
        }

        .lf-title {
          font-size: 18px;
          font-weight: 700;
          color: #181C32;
          line-height: 1.2;
        }

        .lf-subtitle {
          font-size: 12px;
          color: #7e8299;
          margin-top: 4px;
        }

        .lf-close {
          border: none;
          background: #f5f8fa;
          color: #181C32;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 16px;
          line-height: 1;
          display: grid;
          place-items: center;
        }

        .lf-close:hover {
          background: #eef2f7;
        }

        .lf-body {
          padding: 14px 20px 20px 20px;
          overflow: auto;
        }

        .lf-panel .form-label,
        .lf-panel label {
          color: #181c32;
        }

        .lf-footer {
          padding: 12px 20px 18px 20px;
          border-top: 1px solid #eef0f5;
          display: flex;
          justify-content: flex-end;
          gap: 10px;
        }

        .lf-empty {
          padding: 24px;
          background: #f8f9fb;
          border: 1px dashed #dfe3ea;
          border-radius: 12px;
          text-align: center;
        }

        .lf-empty-title {
          font-weight: 700;
          color: #181C32;
          margin-bottom: 6px;
        }

        .lf-empty-subtitle {
          color: #7e8299;
          font-size: 13px;
        }

        @media (max-width: 768px) {
          .lf-overlay { padding: 10px; }
          .lf-panel {
            width: calc(100vw - 20px);
            height: calc(100vh - 20px);
            border-radius: 12px;
          }
          .lf-body { padding: 12px; }
        }
      `}</style>
    </div>
  )
}

export default LibraryFormSubmissionModal
